import React from 'react';

/**
 * Privacy page — plain-language explanation of data handling.
 */
export function PrivacyPage(): React.JSX.Element {
  return (
    <div className="flex flex-col items-center px-4 py-16 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl">
        {/* Prism SVG */}
        <div className="mb-8 flex justify-center">
          <svg
            width="260"
            height="120"
            viewBox="0 0 260 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            className="opacity-90"
          >
            <line x1="0" y1="60" x2="85" y2="60" stroke="#1D1C1D" strokeWidth="3" strokeLinecap="round" />
            <polygon points="90,20 150,20 120,100" fill="none" stroke="#1D1C1D" strokeWidth="2" />
            <line x1="160" y1="28" x2="260" y2="8" stroke="#C75B5B" strokeWidth="2.5" strokeLinecap="round" />
            <line x1="161" y1="35" x2="260" y2="25" stroke="#D4874D" strokeWidth="2.5" strokeLinecap="round" />
            <line x1="162" y1="42" x2="260" y2="42" stroke="#C9A84C" strokeWidth="2.5" strokeLinecap="round" />
            <line x1="162" y1="49" x2="260" y2="58" stroke="#5B9E6F" strokeWidth="2.5" strokeLinecap="round" />
            <line x1="161" y1="56" x2="260" y2="75" stroke="#4D9E9E" strokeWidth="2.5" strokeLinecap="round" />
            <line x1="160" y1="63" x2="260" y2="92" stroke="#4A7FB5" strokeWidth="2.5" strokeLinecap="round" />
            <line x1="158" y1="70" x2="260" y2="108" stroke="#7B5EA7" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        </div>

        <h1 className="mb-2 text-center text-3xl font-bold text-text-primary">
          Privacy
        </h1>
        <p className="mb-3 text-center text-sm font-medium text-text-muted italic">
          Your AAI work conversations stay private and in your control.
        </p>
        <p className="mb-10 text-center text-sm text-text-secondary leading-relaxed max-w-2xl mx-auto">
          Your trust matters. This page explains, in straightforward language, how your data is handled inside AppreciativeAI during the testing period (August 18 – September 18, 2026). No legal jargon — just what&rsquo;s true about how the system works today.
        </p>

        {/* What We Do */}
        <section className="mb-10">
          <h2 className="mb-6 text-xl font-semibold text-text-primary border-b border-border pb-2">
            What We Do to Protect Your Privacy
          </h2>
          <div className="space-y-6">
            <PrivacyItem
              title="Your data stays with us"
              description="Your conversations and account information are stored in our private PostgreSQL database on our EC2 server. We do not sell, share, or transfer your data to any third parties."
            />
            <PrivacyItem
              title="Your agent conversations are private"
              description="Every conversation you have with any of the seven AAI agents is visible only to you. No other user can see your sessions. No admin or team member can browse your conversations through the app."
            />
            <PrivacyItem
              title="Community posts are shared by you intentionally"
              description="The Community space is designed for shared learning. Early Testers can post. Members can read. Nothing from your private agent sessions is ever posted automatically — only what you choose to share."
            />
            <PrivacyItem
              title="Anthropic processes messages, but does not train on them"
              description="When you talk to an AAI agent, your message is sent to Anthropic's Claude API to generate a response. Per Anthropic's published API terms, they do not use API inputs or outputs to train their models. We do not send any additional data beyond the text you provide in the conversation."
            />
            <PrivacyItem
              title="No tracking or advertising cookies"
              description="AppreciativeAI does not use Google Analytics, Facebook Pixel, or any third‑party tracking tools. We do not track your behavior across the web."
            />
            <PrivacyItem
              title="Early Tester data is anonymized after the test period"
              description="If you join as an Early Tester, you agree that anonymized session data may be used to improve the AAI product. At the end of the test period, you'll have one week to copy your data. After that, your account will convert to regular Member access."
            />
          </div>
        </section>

        {/* What We Don't Claim */}
        <section>
          <h2 className="mb-6 text-xl font-semibold text-text-primary border-b border-border pb-2">
            What We Don&rsquo;t Claim (Yet)
          </h2>
          <p className="mb-4 text-sm text-text-secondary leading-relaxed">
            We want to be honest about what is not fully implemented:
          </p>
          <ul className="space-y-3 text-sm text-text-secondary leading-relaxed">
            <li className="flex items-start gap-2">
              <span className="text-text-muted mt-0.5">•</span>
              We are not claiming full GDPR/CCPA compliance — formal legal review will come later.
            </li>
            <li className="flex items-start gap-2">
              <span className="text-text-muted mt-0.5">•</span>
              We do not offer end‑to‑end encryption — your data is encrypted in transit (HTTPS), but not yet encrypted at rest.
            </li>
            <li className="flex items-start gap-2">
              <span className="text-text-muted mt-0.5">•</span>
              We do not yet support full account deletion — the delete‑account flow is still being built.
            </li>
          </ul>
          <p className="mt-6 text-sm text-text-muted italic">
            We&rsquo;ll update this page as these capabilities are added.
          </p>
        </section>
      </div>
    </div>
  );
}

function PrivacyItem({ title, description }: { title: string; description: string }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
      <p className="mt-1 text-sm leading-relaxed text-text-secondary">{description}</p>
    </div>
  );
}
